import { useCallback, useEffect, useMemo, useRef } from 'react'
import { CellEditingStoppedEvent, ColDef } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import clsx from 'clsx'
import { setAlertQueue } from 'cache/alerts'
import { defaultColDef } from 'components/AgGrid'
import { useUpdateDecisionEditPageLines } from 'hooks/use-update-decision-edit-page-lines'
import { CurrencyConvertLineItem, KeyValueResponse } from 'queries/useCurrencyConvertEditPage'
import { MessageType } from 'ts/enums/messageType.enums'
import { ProductLineSummaryTreeNode } from 'ts/interfaces/productLineSummary.interface'
import { useRefreshDataPointsCell } from '../../hooks/useRefreshDataPointsCell'
import type { ColumnKey } from '../../utils/dynamicColorMap'
import { applyDynamicColumnColor, buildDynamicHeaderStyles } from '../../utils/dynamicColorMap'
import { formatUpdateDecisionApiError } from '../../utils/formatUpdateDecisionApiError'
import type { ApprovalOption, ComparisonTab, GuidanceType } from '../ComparisonTabs'
import {
  createDataPointsCellRenderer,
  RowDataPoints,
} from '../DataPoint/createDataPointsCellRenderer'
import type { LinesFilterValue } from '../LinesFilter/LinesFilterDropdown'
import { LINES_FILTER } from '../LinesFilter/LinesFilterDropdown'
import { applyColumnVisibility } from '../shared/applyColumnVisibility'
import { parsePercentageEditValue } from '../validations'
import { frameworkComponents, productLineGridOptions } from './agGridConfig/agGridConfig'
import { getProductLineColDefs } from './agGridConfig/columnDefs'
import styles from './styles.module.scss'
import { transformProductLineSummaryData } from './utils/transformProductLineSummaryData'

interface ProductLineGridProps {
  dealId?: number
  data?: Array<{
    productLineCode: string
    estimatedDiscountPercentage: number
    netMarginPercentage: number
    plDescription?: string | null
    estimatedPnBdNetRevenue?: number | null
    estimatedDiscountPercentageUpdated?: number | null
    updatedCm?: number | null
    cm?: number | null
    busGroupCode?: string | null
    plCategoryCd?: string | null
    expertDiscountPercentage?: number | null
    floorDiscountPercentage?: number | null
    typicalDiscountPercentage?: number | null
  }>
  totals?: {
    totalNetRevenue?: number | null
    totalDiscountPercentage?: number | null
    totalOGMPercentage?: number | null
  }
  currencyData?: CurrencyConvertLineItem[]
  isUSDSelected?: boolean
  dealCurrency?: string
  activeTabs?: ComparisonTab[]
  selectedApprovalTypes?: string[]
  selectedGuidanceTypes?: GuidanceType[]
  colorMap?: Partial<Record<ColumnKey, string>>
  approvalOptions?: ApprovalOption[]
  dealCubeHistoryDetails?: Array<{
    level: string | number
    approversByLevel?: Array<{ decisionReasonCode: string | null }>
    productlineDetails: Array<{
      productLineCode: string
      estimatedDiscountPercentage: number
      netMarginPercentage: number
    }>
  }>
  onRefresh?: () => Promise<unknown>
  onEditSuccess?: () => void
  linesFilter?: LinesFilterValue
  modifiedPlCodes?: Set<string>
  plCostAvailableSet?: Set<string>
}

const productLineRowMapper = (row: ProductLineSummaryTreeNode): RowDataPoints => ({
  baseline: row.dataPoints,
  ogmOrMargin: row.ogmPercent,
  requestedValue: row.requestedOgmPercent ?? row.ogmPercent,
  guidanceValues: {
    expertValue: row.expertDiscountPercentage,
    typicalValue: row.typicalDiscountPercentage,
    floorValue: row.floorDiscountPercentage,
  },
  approvalValues: row.approvalData
    ? Object.fromEntries(
        Object.entries(row.approvalData).map(([id, detail]) => [id, detail.ogmPercent])
      )
    : {},
})

export const ProductLineGrid = ({
  dealId,
  data,
  totals,
  currencyData,
  isUSDSelected,
  dealCurrency,
  activeTabs,
  selectedApprovalTypes,
  selectedGuidanceTypes,
  colorMap,
  approvalOptions,
  dealCubeHistoryDetails,
  onRefresh,
  onEditSuccess,
  linesFilter,
  modifiedPlCodes,
  plCostAvailableSet,
}: ProductLineGridProps) => {
  const gridRef = useRef<AgGridReact<ProductLineSummaryTreeNode>>(null)
  const { updateDecisionEditPageLines } = useUpdateDecisionEditPageLines()

  const refreshGridData = useCallback(() => {
    onEditSuccess?.()

    if (onRefresh) {
      onRefresh().catch((error) => {
        console.error('Failed to refresh data after update:', error)
        setAlertQueue({
          type: MessageType.WARNING,
          message: 'Changes saved but failed to refresh. Please reload the page.',
        })
      })
    }
  }, [onEditSuccess, onRefresh])

  const rollbackEditedCellValue = useCallback(
    (event: CellEditingStoppedEvent<ProductLineSummaryTreeNode>, field: string | undefined) => {
      if (event.node && field && typeof event.node.setDataValue === 'function') {
        event.node.setDataValue(field, event.oldValue)
      }
    },
    []
  )

  const refreshDataPointsCell = useRefreshDataPointsCell<ProductLineSummaryTreeNode>()

  const productLineData = useMemo(() => {
    if (!data || data.length === 0) {
      return null
    }

    let processedData = data
    let convertedTotals = totals

    if (isUSDSelected && currencyData) {
      const categoryConversionMap = new Map<string, number>()
      const plConversionMap = new Map<string, CurrencyConvertLineItem>()

      currencyData.forEach((currItem: CurrencyConvertLineItem) => {
        if (currItem.itemType === 'ProductCategory' && currItem.plCategoryCd) {
          const expectedKey = `EstimatedPnBdNetRevenue${currItem.plCategoryCd}`
          const revenueConversion = currItem.keyValueResponses.find(
            (kvr: KeyValueResponse) => kvr.key === expectedKey
          )
          if (revenueConversion) {
            categoryConversionMap.set(currItem.plCategoryCd, revenueConversion.amount)
          }
        }

        if (currItem.itemType === 'ProductLine' || currItem.lineType === 'PL') {
          plConversionMap.set(currItem.plCode, currItem)
        }
      })

      processedData = data.map((item) => {
        let convertedRevenue = item.estimatedPnBdNetRevenue

        if (item.plCategoryCd && categoryConversionMap.has(item.plCategoryCd)) {
          convertedRevenue =
            categoryConversionMap.get(item.plCategoryCd) ?? item.estimatedPnBdNetRevenue
        }

        const matchingCurrencyItem = plConversionMap.get(item.productLineCode)

        if (matchingCurrencyItem) {
          const expectedKey = `EstimatedPnBdNetRevenue${item.productLineCode}`
          const revenueConversion = matchingCurrencyItem.keyValueResponses.find(
            (kvr: KeyValueResponse) => kvr.key === expectedKey
          )
          if (revenueConversion) {
            convertedRevenue = revenueConversion.amount
          }
        }
        return {
          ...item,
          estimatedPnBdNetRevenue: convertedRevenue,
        }
      })

      const { totalDiscountPercentageSum, totalOGMPercentageSum, totalEstimatedPnBdNetRevenueSum } =
        currencyData?.[0] ?? {}
      convertedTotals = {
        totalNetRevenue: totalEstimatedPnBdNetRevenueSum,
        totalDiscountPercentage: totalDiscountPercentageSum,
        totalOGMPercentage: totalOGMPercentageSum,
      }
    }

    return transformProductLineSummaryData(
      processedData,
      dealCubeHistoryDetails ?? [],
      convertedTotals
    )
  }, [data, currencyData, isUSDSelected, dealCubeHistoryDetails, totals])

  const customDefaultColDef: ColDef = useMemo(
    () => ({
      ...defaultColDef,
      sortable: false,
      filter: false,
    }),
    []
  )

  const rowData = useMemo(() => {
    if (!productLineData) return []
    return productLineData.items
  }, [productLineData])

  const pinnedBottomRowData = useMemo(() => {
    if (linesFilter === LINES_FILTER.OPEN || linesFilter === LINES_FILTER.MY) return []
    if (!productLineData?.totals) return []
    return [
      {
        id: 'totals',
        hierarchyPath: ['Total'],
        hierarchyCode: 'Total',
        description: '',
        netRevenue: productLineData.totals.netRevenue,
        dataPoints: undefined,
        totalDiscountPercentUpdated: productLineData.totals.totalDiscountPercent,
        ogmPercent: productLineData.totals.ogmPercent,
        level: 0,
      },
    ]
  }, [productLineData, linesFilter])

  const columnDefs = useMemo<ColDef<ProductLineSummaryTreeNode>[]>(() => {
    const showRequestedPriceTab = activeTabs?.includes('requestedPrice') ?? false
    const guidanceSelections = selectedGuidanceTypes ?? []
    const showFloorDiscount = guidanceSelections.includes('floor')
    const showExpertDiscount = guidanceSelections.includes('expert')
    const showTypicalDiscount = guidanceSelections.includes('typical')

    const approvalPrefixHideMap: Partial<Record<string, boolean>> = {}
    approvalOptions?.forEach(({ id }) => {
      approvalPrefixHideMap[`approval-${id}-`] = !(selectedApprovalTypes?.includes(id) ?? false)
    })

    return getProductLineColDefs(showRequestedPriceTab, approvalOptions).map((col) => {
      if (col.field === 'dataPoints') {
        return {
          ...col,
          cellRenderer: createDataPointsCellRenderer(
            productLineRowMapper,
            showRequestedPriceTab,
            selectedApprovalTypes,
            guidanceSelections,
            colorMap
          ),
        }
      }

      const withVisibility =
        applyColumnVisibility(
          col,
          {
            floorDiscountPercentage: !showFloorDiscount,
            expertDiscountPercentage: !showExpertDiscount,
            typicalDiscountPercentage: !showTypicalDiscount,
          },
          {
            'requested-': !showRequestedPriceTab,
            ...approvalPrefixHideMap,
          }
        ) ?? col

      return colorMap ? applyDynamicColumnColor(withVisibility, colorMap) : withVisibility
    })
  }, [activeTabs, selectedApprovalTypes, selectedGuidanceTypes, colorMap, approvalOptions])

  const handleCellEditingStopped = useCallback(
    (event: CellEditingStoppedEvent<ProductLineSummaryTreeNode>) => {
      const editedValue = event.newValue ?? event.value

      if (!dealId || isUSDSelected || event.node?.rowPinned || event.node?.group) {
        return
      }

      const field = event.colDef.field
      if (field !== 'ogmPercent' && field !== 'totalDiscountPercentUpdated') {
        return
      }

      if (editedValue === event.oldValue) {
        return
      }

      const parsedValue = parsePercentageEditValue(editedValue)

      if (parsedValue === null) {
        rollbackEditedCellValue(event, field)
        refreshDataPointsCell(event)
        return
      }

      const plCode = event.data?.hierarchyCode

      if (!plCode || plCode === 'Total' || event.data?.level !== 2) {
        return
      }

      updateDecisionEditPageLines({
        variables: {
          updateRequest: {
            dealId,
            isPLTotalView: true,
            productUpdates: [
              {
                lineNumber: null,
                requestNetMarginPercentage: null,
                authBdNetPriceAmount: null,
                authNetMarginPercentage: null,
                ...(field === 'totalDiscountPercentUpdated'
                  ? { totalDiscountAuthPercentage: parsedValue }
                  : { ogmPercentage: parsedValue }),
                plCode,
              },
            ],
          },
        },
      })
        .then((result) => {
          const updateResult = result.data?.updateDecisionEditPageLines
          const apiError = formatUpdateDecisionApiError(updateResult?.error, {
            label: 'PL',
            value: plCode,
          })

          if (!updateResult?.isSuccess) {
            if (apiError) {
              setAlertQueue({
                type: MessageType.ERROR,
                message: apiError,
              })
            }
            rollbackEditedCellValue(event, field)
            refreshDataPointsCell(event)
            return
          }

          if (apiError) {
            setAlertQueue({
              type: MessageType.ERROR,
              message: apiError,
            })
          }
          refreshDataPointsCell(event)
          refreshGridData()
        })
        .catch((error) => {
          console.error('Failed to update decision edit page lines:', error)

          setAlertQueue({
            type: MessageType.ERROR,
            message: 'Failed to save changes. Please try again.',
          })
          rollbackEditedCellValue(event, field)
          refreshDataPointsCell(event)
        })
    },
    [
      dealId,
      isUSDSelected,
      updateDecisionEditPageLines,
      refreshGridData,
      rollbackEditedCellValue,
      refreshDataPointsCell,
    ]
  )

  useEffect(() => {
    gridRef.current?.api?.stopEditing(true)
    gridRef.current?.api?.refreshCells({ force: true })
  }, [isUSDSelected, linesFilter, plCostAvailableSet])

  return (
    <section
      className={clsx('ag-theme-alpine', styles.productLineGridContainer)}
      aria-label="Product line summary table"
    >
      {colorMap && Object.keys(colorMap).length > 0 && (
        // eslint-disable-next-line react/no-danger
        <style dangerouslySetInnerHTML={{ __html: buildDynamicHeaderStyles(colorMap) }} />
      )}
      <AgGridReact<ProductLineSummaryTreeNode>
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={customDefaultColDef}
        pinnedBottomRowData={pinnedBottomRowData}
        context={{
          isUSDSelected,
          dealCurrency,
          isReadOnly: linesFilter === LINES_FILTER.ALL,
          linesFilter,
          modifiedPlCodes,
          plCostAvailableSet,
        }}
        onCellEditingStopped={handleCellEditingStopped}
        components={frameworkComponents}
        {...productLineGridOptions}
      />
    </section>
  )
}
